/* eslint-disable @typescript-eslint/no-explicit-any */
"use client"

import { useEffect, useState } from "react"
import type { ChangeEvent, FormEvent } from "react"
import Link from "next/link"
import { format } from "date-fns"
import axiosInstance from "@/lib/utils/axiosInstance"
import { getApplicationClasses, type ApplicationClass } from "@/services/application-class-service"
import { Button } from "@/components/ui/button"
import { PhoneInput as ShadcnPhoneInput } from "@/components/phone-input"
import { isValidPhoneNumber, isPossiblePhoneNumber } from "react-phone-number-input"
import { CheckCircle2, AlertCircle, CalendarIcon } from "lucide-react"
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Calendar } from "@/components/ui/calendar"
import { Combobox } from "@/components/ui/combobox"
import { US_STATE_OPTIONS } from "@/lib/constants/us-state-codes"
import { cn } from "@/lib/utils"

interface UtmParams {
  utm_source: string
  utm_medium: string
  utm_campaign: string
  utm_term: string
  gclid: string
}

interface NpApplicationFormData extends UtmParams {
  name: string
  email: string
  phoneNumber: string
  whatsapp: string
  gender: string
  university: string
  specialtyTrack: string
  selectedClass: string
  preceptorPreference: string
  preceptorSpecialty: string
  hoursRequired: string
  startDate: string
  state: string
  zipCode: string
  previousExperience: string
  messageForAce: string
  telemedicine: string
  referredBy: string
  friendsInterested: string
  acknowledgment: boolean
}

const WHATSAPP_OPTIONS = [
  { value: "Yes", label: "Yes" },
  { value: "I can download it", label: "Can download" },
  { value: "I prefer to avoid using WhatsApp if possible", label: "Prefer not to" },
]

const GENDER_OPTIONS = [
  { value: "Female", label: "Female" },
  { value: "Male", label: "Male" },
  { value: "Other", label: "Other" },
]

const UNIVERSITY_OPTIONS = [
  "Walden University",
  "Chamberlain University",
  "Purdue University",
  "Maryville University",
  "George Washington University",
  "National University",
  "Charles Drew University",
  "West Coast University",
  "University of South Alabama",
  "Herzing University",
  "Brandman/UMass Global",
  "Liberty University",
  "Wilkes University",
  "Bowie State University",
  "Husson University",
  "Grand Canyon University",
  "University of Southern Indiana",
  "University of Alabama",
  "William Paterson University",
  "Drexel University",
  "Western Governors University",
  "Spring Arbor University",
  "Regis College",
  "Samford University",
  "Rocky Mountain University",
  "Western University of Health Sciences",
  "Samuel Merritt University",
  "United States University",
  "Other",
].map((u) => ({ value: u, label: u }))

const SPECIALTY_TRACK_OPTIONS = [
  { value: "PMHNP", label: "PMHNP" },
  { value: "FNP", label: "FNP" },
  { value: "AGPCNP", label: "AGPCNP" },
  { value: "DNP", label: "DNP" },
  { value: "AGACNP", label: "AGACNP" },
  { value: "Other", label: "Other" },
]

// Class options are admin-managed (Settings → Class Mapping → NP Classes) and
// fetched from the API at runtime.

const PRECEPTOR_PREFERENCE_OPTIONS = [
  { value: "Medical Doctor", label: "Medical Doctor" },
  { value: "Nurse Practitioner", label: "Nurse Practitioner" },
  { value: "Other (Therapist/Social worker)", label: "Therapist / Social Worker" },
]

const PRECEPTOR_SPECIALTY_OPTIONS = [
  { value: "Internal Medicine", label: "Internal Medicine" },
  { value: "Family Medicine", label: "Family Medicine" },
  { value: "Pediatrics", label: "Pediatrics" },
  { value: "OB/GYN (Women's Health)", label: "OB/GYN" },
  { value: "Psychiatry", label: "Psychiatry" },
  { value: "Therapy", label: "Therapy" },
  { value: "Other", label: "Other" },
  { value: "I'm not sure", label: "Not sure" },
]

const TELEMEDICINE_OPTIONS = [
  { value: "Yes", label: "Yes" },
  { value: "No, I want to avoid tele-medicine learning", label: "In-person only" },
  { value: "I have not decided but I am open to the possibility", label: "Not decided" },
]

const HOURS_PRESETS = ["90", "160", "240"]

const STATE_COMBOBOX_OPTIONS = US_STATE_OPTIONS.map((s) => ({
  value: s.code,
  label: `${s.code} — ${s.name}`,
}))

const inputClass =
  "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"

const toggleItemClass =
  "border border-gray-200 bg-white text-gray-700 hover:bg-blue-50 hover:text-blue-700 data-[state=on]:bg-blue-600 data-[state=on]:text-white data-[state=on]:border-blue-600 data-[state=on]:hover:bg-blue-700"

const initialFormData: NpApplicationFormData = {
  name: "",
  email: "",
  phoneNumber: "",
  whatsapp: "",
  gender: "",
  university: "",
  specialtyTrack: "",
  selectedClass: "",
  preceptorPreference: "",
  preceptorSpecialty: "",
  hoursRequired: "",
  startDate: "",
  state: "",
  zipCode: "",
  previousExperience: "",
  messageForAce: "",
  telemedicine: "",
  referredBy: "",
  friendsInterested: "",
  acknowledgment: false,
  utm_source: "",
  utm_medium: "",
  utm_campaign: "",
  utm_term: "",
  gclid: "",
}

export function NpApplicationFormCompact() {
  const [formData, setFormData] = useState<NpApplicationFormData>(initialFormData)
  const [classOptions, setClassOptions] = useState<ApplicationClass[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [emailTouched, setEmailTouched] = useState(false)
  const [phoneError, setPhoneError] = useState("")
  const [datePopoverOpen, setDatePopoverOpen] = useState(false)

  useEffect(() => {
    getApplicationClasses("np")
      .then(setClassOptions)
      .catch((err) => console.error("Failed to load NP classes:", err))
  }, [])

  // Capture UTM / gclid from URL on mount.
  useEffect(() => {
    if (typeof window === "undefined") return
    const params = new URLSearchParams(window.location.search)
    const captured: Partial<UtmParams> = {}
    for (const key of ["utm_source", "utm_medium", "utm_campaign", "utm_term", "gclid"] as const) {
      const value = params.get(key)
      if (value) captured[key] = value
    }
    if (Object.keys(captured).length) {
      setFormData((prev) => ({ ...prev, ...captured }))
    }
  }, [])

  const isEmailFormatValid = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)

  const getEmailValidationMessage = (value: string) => {
    const trimmed = value.trim()
    if (!trimmed) return "Email is required."
    if (!isEmailFormatValid(trimmed)) return "Please enter a valid email address."
    return ""
  }

  const validatePhoneNumber = (phone: string): boolean => {
    if (!phone) return false
    try {
      return isValidPhoneNumber(phone)
    } catch (err) {
      console.error("Phone validation error", err)
      return false
    }
  }

  const isPossiblePhone = (phone: string): boolean => {
    if (!phone) return false
    try {
      return isPossiblePhoneNumber(phone)
    } catch (err) {
      console.error("Phone validation error", err)
      return false
    }
  }

  const getPhoneValidationMessage = (phone: string): string => {
    if (!phone) return ""
    if (!isPossiblePhone(phone)) return "Phone number appears incomplete. Please check the format."
    if (!validatePhoneNumber(phone)) return "This doesn't appear to be a valid phone number for the selected country."
    return ""
  }

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handlePhoneChange = (value: string | undefined) => {
    const phoneValue = value ?? ""
    setFormData((prev) => ({ ...prev, phoneNumber: phoneValue }))
    setPhoneError(phoneValue ? getPhoneValidationMessage(phoneValue) : "")
  }

  const setField = (name: keyof NpApplicationFormData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [name]: value }) as NpApplicationFormData)
  }

  const startDateAsDate = formData.startDate ? new Date(`${formData.startDate}T00:00:00`) : undefined

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setEmailTouched(true)

    const emailValidationMessage = getEmailValidationMessage(formData.email)
    if (emailValidationMessage) {
      setError(emailValidationMessage)
      return
    }
    if (!validatePhoneNumber(formData.phoneNumber)) {
      setError("Please enter a valid phone number (include country code if needed).")
      return
    }
    if (!formData.name.trim()) {
      setError("Please enter your name.")
      return
    }
    if (!formData.acknowledgment) {
      setError("Please accept the SMS terms to submit.")
      return
    }

    setLoading(true)

    try {
      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        phoneNumber: formData.phoneNumber,
        whatsapp: formData.whatsapp || null,
        gender: formData.gender || null,
        university: formData.university || null,
        specialtyTrack: formData.specialtyTrack || null,
        selectedClass: formData.selectedClass || null,
        preceptorPreference: formData.preceptorPreference || null,
        preceptorSpecialty: formData.preceptorSpecialty || null,
        hoursRequired: formData.hoursRequired || null,
        startDate: formData.startDate || null,
        state: formData.state || null,
        zipCode: formData.zipCode.trim() || null,
        previousExperience: formData.previousExperience.trim() || null,
        messageForAce: formData.messageForAce.trim() || null,
        telemedicine: formData.telemedicine || null,
        referredBy: formData.referredBy.trim() || null,
        friendsInterested: formData.friendsInterested.trim() || null,
        acknowledgment: formData.acknowledgment,
        utm_source: formData.utm_source || null,
        utm_medium: formData.utm_medium || null,
        utm_campaign: formData.utm_campaign || null,
        utm_term: formData.utm_term || null,
        gclid: formData.gclid || null,
      }

      const response = await axiosInstance.post("/np-applications/intake", payload)

      if (response.data) {
        setSuccess(true)
      }
    } catch (err: any) {
      const validationErrors = err.response?.data?.errors
      const firstFieldError = validationErrors
        ? Object.values(validationErrors).find((messages) => Array.isArray(messages) && messages.length > 0)
        : null

      setError(
        (Array.isArray(firstFieldError) ? firstFieldError[0] : null) ||
          err.response?.data?.message ||
          err.message ||
          "Failed to submit application. Please try again."
      )
    } finally {
      setLoading(false)
    }
  }

  const emailValidationMessage = getEmailValidationMessage(formData.email)
  const emailMessage = emailTouched ? emailValidationMessage : ""
  const emailReady = Boolean(formData.email.trim()) && !emailValidationMessage

  const isFormComplete =
    Boolean(formData.name.trim()) &&
    emailReady &&
    validatePhoneNumber(formData.phoneNumber) &&
    formData.acknowledgment &&
    !loading

  if (success) {
    return (
      <div className="text-center space-y-3 py-4">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 text-blue-600 text-2xl">
          ✓
        </div>
        <h3 className="font-semibold text-blue-700">Thank you!</h3>
        <p className="text-xs text-gray-600 max-w-sm mx-auto">
          We will contact you soon and look forward to helping. If you want to skip the wait, call{" "}
          <a href="tel:+17034892336" className="font-semibold text-blue-700 hover:underline">
            (703) 489-2336
          </a>{" "}
          now to get started.
        </p>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {error && (
        <div className="p-3 rounded-lg bg-red-50 border border-red-200">
          <p className="text-xs text-red-800">{error}</p>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Name *</label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            placeholder="Jane Doe"
            className={inputClass}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Personal Email Address *</label>
          <div className="relative">
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              onBlur={() => setEmailTouched(true)}
              required
              placeholder="jane@example.com"
              className={cn(
                "w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500",
                emailTouched && !emailMessage && "border-emerald-500 focus:ring-emerald-500",
                emailTouched && Boolean(emailMessage) && "border-red-500 focus:ring-red-500"
              )}
            />
            {emailTouched && !emailMessage && (
              <CheckCircle2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-emerald-500" />
            )}
            {emailTouched && Boolean(emailMessage) && (
              <AlertCircle className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-red-500" />
            )}
          </div>
          {emailTouched && emailMessage && (
            <small className="mt-1 block text-[10px] text-red-600" aria-live="polite">
              {emailMessage}
            </small>
          )}
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Phone Number *</label>
        <div className={cn("rounded-lg", phoneError && "ring-2 ring-red-500")}>
          <ShadcnPhoneInput
            value={formData.phoneNumber || undefined}
            onChange={handlePhoneChange}
            defaultCountry="US"
            international={false}
            addInternationalOption={false}
            name="phoneNumber"
            required
            className="w-full"
          />
        </div>
        {phoneError && (
          <small className="mt-1 text-[10px] text-red-600 flex items-center gap-1" aria-live="polite">
            <AlertCircle className="h-3 w-3" />
            {phoneError}
          </small>
        )}
        {!phoneError && formData.phoneNumber && validatePhoneNumber(formData.phoneNumber) && (
          <small className="mt-1 text-[10px] text-emerald-600 flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3" />
            Phone number looks good
          </small>
        )}
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Do you have WhatsApp?</label>
        <small className="block text-[10px] text-gray-500 mb-2">
          Once trainees sign up we make a group chat on WhatsApp so our team can work with you most directly and efficiently.
        </small>
        <ToggleGroup
          type="single"
          value={formData.whatsapp}
          onValueChange={(v) => setField("whatsapp", v)}
          spacing={2}
          className="flex-wrap"
        >
          {WHATSAPP_OPTIONS.map((opt) => (
            <ToggleGroupItem key={opt.value} value={opt.value} className={toggleItemClass}>
              {opt.label}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-2">Gender</label>
          <ToggleGroup
            type="single"
            value={formData.gender}
            onValueChange={(v) => setField("gender", v)}
            spacing={2}
            className="flex-wrap"
          >
            {GENDER_OPTIONS.map((opt) => (
              <ToggleGroupItem key={opt.value} value={opt.value} className={toggleItemClass}>
                {opt.label}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-2">Specialty Track of Student</label>
          <ToggleGroup
            type="single"
            value={formData.specialtyTrack}
            onValueChange={(v) => setField("specialtyTrack", v)}
            spacing={2}
            className="flex-wrap"
          >
            {SPECIALTY_TRACK_OPTIONS.map((opt) => (
              <ToggleGroupItem key={opt.value} value={opt.value} className={toggleItemClass}>
                {opt.label}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">University</label>
        <small className="block text-[10px] text-gray-500 mb-2">Choose your program.</small>
        <Combobox
          items={UNIVERSITY_OPTIONS}
          value={formData.university}
          onValueChange={(v) => setField("university", v)}
          placeholder="Select university"
          searchPlaceholder="Search universities..."
          emptyText="No university found."
        />
      </div>

      <fieldset>
        <legend className="block text-xs font-medium text-gray-700 mb-1">Select your class</legend>
        <small className="block text-[10px] text-gray-500 mb-2">For Walden PMHNP / FNP programs.</small>
        <div className="space-y-2">
          {classOptions.map((opt) => {
            const selected = formData.selectedClass === opt.value
            return (
              <label
                key={opt.value}
                className={cn(
                  "flex items-start gap-3 cursor-pointer rounded-lg border px-3 py-2.5 transition-colors",
                  selected
                    ? "border-blue-500 bg-blue-50/60 ring-1 ring-blue-200"
                    : "border-gray-200 hover:border-blue-200 hover:bg-blue-50/30"
                )}
              >
                <input
                  type="radio"
                  name="selectedClass"
                  value={opt.value}
                  checked={selected}
                  onChange={handleChange}
                  className="mt-1 h-3.5 w-3.5 text-blue-600 focus:ring-blue-500"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-semibold text-slate-900">{opt.code}</span>
                    <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600">
                      {opt.program}
                    </span>
                  </div>
                  <p className="mt-0.5 text-sm text-slate-700">{opt.title}</p>
                </div>
              </label>
            )
          })}
        </div>
      </fieldset>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">MD or NP preference</label>
          <small className="block text-[10px] text-gray-500 mb-2">
            Do you prefer an MD or NP as preceptor?
          </small>
          <ToggleGroup
            type="single"
            value={formData.preceptorPreference}
            onValueChange={(v) => setField("preceptorPreference", v)}
            spacing={2}
            className="flex-wrap"
          >
            {PRECEPTOR_PREFERENCE_OPTIONS.map((opt) => (
              <ToggleGroupItem key={opt.value} value={opt.value} className={toggleItemClass}>
                {opt.label}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Preceptor Specialty</label>
          <small className="block text-[10px] text-gray-500 mb-2">
            Choose the specialty of the preceptor that you need.
          </small>
          <ToggleGroup
            type="single"
            value={formData.preceptorSpecialty}
            onValueChange={(v) => setField("preceptorSpecialty", v)}
            spacing={2}
            className="flex-wrap"
          >
            {PRECEPTOR_SPECIALTY_OPTIONS.map((opt) => (
              <ToggleGroupItem key={opt.value} value={opt.value} className={toggleItemClass}>
                {opt.label}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Number of Hours Required</label>
          <small className="block text-[10px] text-gray-500 mb-2">
            How many hours are required for your class? For example: 240
          </small>
          <input
            type="number"
            name="hoursRequired"
            value={formData.hoursRequired}
            onChange={handleChange}
            min="0"
            placeholder="e.g. 240"
            className={inputClass}
          />
          <div className="mt-2 flex flex-wrap gap-1.5">
            <span className="text-[10px] text-gray-500 self-center">Common:</span>
            {HOURS_PRESETS.map((preset) => {
              const active = formData.hoursRequired === preset
              return (
                <button
                  key={preset}
                  type="button"
                  onClick={() => setField("hoursRequired", preset)}
                  className={cn(
                    "rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition-colors",
                    active
                      ? "border-blue-500 bg-blue-600 text-white"
                      : "border-gray-200 bg-white text-gray-700 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700"
                  )}
                >
                  {preset}
                </button>
              )
            })}
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Start Date for class</label>
          <small className="block text-[10px] text-gray-500 mb-2">When will your class begin?</small>
          <Popover open={datePopoverOpen} onOpenChange={setDatePopoverOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-normal border-gray-300",
                  !startDateAsDate && "text-gray-500"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {startDateAsDate ? format(startDateAsDate, "MMM d, yyyy") : "Pick a date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={startDateAsDate}
                onSelect={(d) => {
                  if (d) {
                    setField("startDate", format(d, "yyyy-MM-dd"))
                    setDatePopoverOpen(false)
                  }
                }}
                captionLayout="dropdown"
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">State for rotation</label>
          <small className="block text-[10px] text-gray-500 mb-2">
            Where you live or want to do your rotation.
          </small>
          <Combobox
            items={STATE_COMBOBOX_OPTIONS}
            value={formData.state}
            onValueChange={(v) => setField("state", v.toUpperCase())}
            placeholder="Select state"
            searchPlaceholder="Search states..."
            emptyText="No state found."
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Zip Code</label>
          <small className="block text-[10px] text-gray-500 mb-2">Zip code of where you live.</small>
          <input
            type="text"
            name="zipCode"
            value={formData.zipCode}
            onChange={handleChange}
            inputMode="numeric"
            pattern="\d{5}"
            maxLength={5}
            placeholder="e.g. 19901"
            className={inputClass}
          />
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">
          Open to tele-medicine rotation?
        </label>
        <small className="block text-[10px] text-gray-500 mb-2">
          Some universities allow part or all of the rotation to be delivered remotely.
        </small>
        <ToggleGroup
          type="single"
          value={formData.telemedicine}
          onValueChange={(v) => setField("telemedicine", v)}
          spacing={2}
          className="flex-wrap"
        >
          {TELEMEDICINE_OPTIONS.map((opt) => (
            <ToggleGroupItem key={opt.value} value={opt.value} className={toggleItemClass}>
              {opt.label}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Previous NP Rotation Experience</label>
        <small className="block text-[10px] text-gray-500 mb-2">
          Please list preceptors or sites you have used already.
        </small>
        <textarea
          name="previousExperience"
          value={formData.previousExperience}
          onChange={handleChange}
          rows={3}
          placeholder="List previous preceptors, clinical sites, or rotations."
          className={inputClass}
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Message for ACE</label>
        <small className="block text-[10px] text-gray-500 mb-2">
          You can leave any important message for us here.
        </small>
        <textarea
          name="messageForAce"
          value={formData.messageForAce}
          onChange={handleChange}
          rows={3}
          placeholder="Anything you'd like our team to know."
          className={inputClass}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Referred by</label>
          <small className="block text-[10px] text-gray-500 mb-2">
            If you wish to list the person who referred you.
          </small>
          <input
            type="text"
            name="referredBy"
            value={formData.referredBy}
            onChange={handleChange}
            placeholder="Name (optional)"
            className={inputClass}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Friends or Colleagues also Interested
          </label>
          <small className="block text-[10px] text-gray-500 mb-2">
            Their names and phone numbers.
          </small>
          <textarea
            name="friendsInterested"
            value={formData.friendsInterested}
            onChange={handleChange}
            rows={2}
            placeholder="Name — phone number (one per line)"
            className={inputClass}
          />
        </div>
      </div>

      <div className="rounded-lg border border-blue-200 bg-blue-50/50 p-4">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.acknowledgment}
            onChange={(e) => setField("acknowledgment", e.target.checked)}
            required
            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-[11px] leading-relaxed text-slate-700">
            I agree to receive recurring automated text messages at the phone number provided. Msg &amp; data rates
            may apply. Msg frequency varies. Reply HELP for help and STOP to cancel. View our{" "}
            <Link
              href="/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-blue-700 hover:underline"
            >
              Terms of Service
            </Link>{" "}
            and{" "}
            <Link
              href="/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-blue-700 hover:underline"
            >
              Privacy Policy
            </Link>
            . *
          </span>
        </label>
      </div>

      <Button
        type="submit"
        disabled={loading || !isFormComplete}
        className={cn(
          "w-full bg-gradient-to-r from-blue-600 to-sky-500 hover:from-blue-700 hover:to-sky-600 text-white font-medium py-2 rounded-lg text-sm transition-all",
          (loading || !isFormComplete) && "opacity-70 cursor-not-allowed"
        )}
      >
        {loading ? "Submitting..." : "Submit"}
      </Button>

      <small className="block text-center text-[10px] text-gray-500">
        The team will review your application.
      </small>
    </form>
  )
}
